import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/layouts/DashboardLayout';

const RecentActivityPage = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'needs_review', 'mock_test', 'practice'

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/client/attempts/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) throw new Error('Không thể tải dữ liệu lịch sử');
        
        const result = await response.json();
        // Assuming API returns { data: [...] }
        setActivities(result.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    }).format(date);
  };

  const getTestTypeInfo = (type) => {
    if (type === 'personalized') return { label: 'Skill Practice', icon: 'menu_book', color: 'text-purple-600', bg: 'bg-purple-100' };
    return { label: 'Mock Test', icon: 'quiz', color: 'text-green-600', bg: 'bg-green-100' };
  };

  // Lọc dữ liệu
  const filteredActivities = useMemo(() => {
    return activities.filter(act => {
      const scorePercentage = act.max_score > 0 ? (act.total_score / act.max_score) * 100 : 0;
      
      if (filter === 'needs_review') return act.completed_at && scorePercentage < 80; // Dưới 80% là cần ôn lại
      if (filter === 'mock_test') return act.test_type === 'exam';
      if (filter === 'practice') return act.test_type === 'personalized';
      return true; // 'all'
    });
  }, [activities, filter]);

  if (loading) return <DashboardLayout><div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div></div></DashboardLayout>;
  if (error) return <DashboardLayout><div className="p-8 text-center text-red-500">Lỗi: {error}</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-900 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          
          {/* Header & Filters */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-black font-display text-slate-800 dark:text-white flex items-center gap-3">
                 <span className="material-symbols-outlined text-primary-blue text-4xl">history_edu</span>
                 Lịch sử & Ôn tập
              </h1>
              <p className="text-slate-500 mt-2">Xem lại các bài đã làm và khắc phục những lỗi sai để tiến bộ nhanh hơn.</p>
            </div>

            <div className="flex bg-white dark:bg-slate-800 p-1.5 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
              <button 
                onClick={() => setFilter('all')}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${filter === 'all' ? 'bg-slate-100 text-slate-800 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                Tất cả
              </button>
              <button 
                onClick={() => setFilter('needs_review')}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-all flex items-center gap-1 ${filter === 'needs_review' ? 'bg-red-50 text-red-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                <span className="material-symbols-outlined text-[16px]">error</span>
                Cần ôn tập
              </button>
            </div>
          </div>

          {/* Grid Layout */}
          {filteredActivities.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-300">
              <span className="material-symbols-outlined text-6xl text-slate-300 mb-4">inventory_2</span>
              <h3 className="text-xl font-bold text-slate-700">Không tìm thấy bài làm nào!</h3>
              <p className="text-slate-500 mt-2">Hãy thử thay đổi bộ lọc hoặc bắt đầu làm một bài kiểm tra mới.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredActivities.map(activity => {
                const isCompleted = !!activity.completed_at;
                const typeInfo = getTestTypeInfo(activity.test_type);
                const scorePercentage = activity.max_score > 0 ? Math.round((activity.total_score / activity.max_score) * 100) : 0;
                
                // Determine styling based on performance
                const isNeedsReview = isCompleted && scorePercentage < 80;
                const cardBorder = isNeedsReview ? 'border-red-200 dark:border-red-900/50' : 'border-slate-200 dark:border-slate-700';
                const cardBg = isNeedsReview ? 'bg-red-50/30' : 'bg-white';
                
                const baseUrl = activity.test_type === 'personalized' ? '/personalized-tests' : '/full-mock-test';
                // For review, point to the result summary first if it's a mock test
                const targetUrl = isCompleted 
                  ? (activity.test_type === 'personalized' ? `${baseUrl}/${activity.id}/review` : `${baseUrl}/result/${activity.id}`)
                  : `${baseUrl}/${activity.test_id}/attempt`;

                return (
                  <div key={activity.id} className={`flex flex-col p-6 rounded-3xl border-2 ${cardBorder} ${cardBg} shadow-sm hover:shadow-lg transition-shadow relative overflow-hidden group`}>
                    
                    {/* Status Badge */}
                    <div className="flex justify-between items-start mb-4">
                       <div className={`px-2.5 py-1 rounded-md text-xs font-bold flex items-center gap-1.5 ${typeInfo.bg} ${typeInfo.color}`}>
                          <span className="material-symbols-outlined text-[14px]">{typeInfo.icon}</span>
                          {typeInfo.label}
                       </div>
                       {!isCompleted && (
                         <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-amber-100 text-amber-700 animate-pulse">
                            Đang làm dở
                         </span>
                       )}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2 line-clamp-2 leading-snug">
                        {activity.title}
                      </h3>
                      <p className="text-sm text-slate-500 font-medium flex items-center gap-1.5 mb-6">
                        <span className="material-symbols-outlined text-[16px]">calendar_month</span>
                        {formatDate(activity.started_at)}
                      </p>
                    </div>

                    {/* Score & Action Row */}
                    <div className="mt-auto pt-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                       {isCompleted ? (
                         <div className="flex flex-col">
                           <span className="text-[10px] uppercase font-bold text-slate-400">Kết quả</span>
                           <span className={`text-2xl font-black ${scorePercentage >= 80 ? 'text-green-500' : 'text-red-500'}`}>
                             {scorePercentage}%
                           </span>
                         </div>
                       ) : (
                         <div className="text-sm font-bold text-slate-400">Chưa có điểm</div>
                       )}

                       <Link 
                          to={targetUrl}
                          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition-all ${
                            isNeedsReview 
                            ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                            : isCompleted 
                              ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                              : 'bg-primary-blue text-white hover:bg-blue-600 shadow-md'
                          }`}
                       >
                          {isNeedsReview ? (
                            <><span>Ôn Lỗi Sai</span> <span className="material-symbols-outlined text-sm">plumbing</span></>
                          ) : isCompleted ? (
                            <><span>Xem Lại</span> <span className="material-symbols-outlined text-sm">visibility</span></>
                          ) : (
                            <><span>Làm Tiếp</span> <span className="material-symbols-outlined text-sm">play_arrow</span></>
                          )}
                       </Link>
                    </div>

                    {/* Needs Review Decoration */}
                    {isNeedsReview && (
                      <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden pointer-events-none">
                         <div className="absolute top-0 right-0 w-2 h-full bg-red-400"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RecentActivityPage;